/**
 * Theo Documentation merge helpers (help-center capability index).
 *
 * Components / concepts: documentation merge, RMM remote session, disabled scripts.
 *
 * Static `theo-capabilities.md` holds baseline platform APIs. Live Datto/NinjaOne
 * Remote Session + Scripts/Automations subsections come from `/help-center/documentation/rmm-capabilities`
 * (MSP `platform.config_json`: `reverse_ssh_*` + `disabled_script_components`).
 * Static “(already enabled)” placeholders under Datto/NinjaOne are stripped before merge.
 */

export type DocActionItem = {
  name: string;
  description: string;
};

export type DocSubsection = {
  id: string;
  title: string;
  actions: DocActionItem[];
};

export type DocSection = {
  id: string;
  title: string;
  subsections: DocSubsection[];
};

/** Static Datto/Ninja subsections that must never appear — live config owns these. */
const STATIC_RMM_PLACEHOLDER_TITLE_RE =
  /already enabled|shell session (components|scripts)|enabled windows scripts/i;

const RMM_PLATFORM_SECTION_IDS = new Set(["datto", "ninjaone"]);

export const cloneDocumentationSection = (section: DocSection): DocSection => ({
  ...section,
  subsections: section.subsections.map((subsection) => ({
    ...subsection,
    actions: subsection.actions.map((action) => ({ ...action })),
  })),
});

export const cloneDocumentationSections = (sections: DocSection[]): DocSection[] =>
  sections.map(cloneDocumentationSection);

/**
 * Drop leftover static placeholders under Datto/NinjaOne before merging live config.
 */
export const stripStaticRmmDocumentationPlaceholders = (sections: DocSection[]): DocSection[] =>
  sections.map((section) => {
    if (!RMM_PLATFORM_SECTION_IDS.has(section.id)) {
      return section;
    }
    return {
      ...section,
      subsections: section.subsections.filter(
        (subsection) => !STATIC_RMM_PLACEHOLDER_TITLE_RE.test(subsection.title)
      ),
    };
  });

/**
 * Merge baseline markdown sections with MSP-specific RMM Remote Session / Scripts.
 * Dynamic subsections insert before "Other" when present.
 */
export const mergeDocumentationSections = (
  baseSections: DocSection[],
  dynamicSections: DocSection[]
): DocSection[] => {
  const sections = cloneDocumentationSections(stripStaticRmmDocumentationPlaceholders(baseSections));

  if (dynamicSections.length === 0) {
    return sections;
  }

  for (const dynamicSection of dynamicSections) {
    let targetSection = sections.find(
      (section) =>
        section.id === dynamicSection.id ||
        section.title.toLowerCase() === dynamicSection.title.toLowerCase()
    );

    if (!targetSection) {
      sections.push(cloneDocumentationSection(dynamicSection));
      continue;
    }

    for (const dynamicSubsection of dynamicSection.subsections) {
      const existingSubsection = targetSection.subsections.find(
        (subsection) =>
          subsection.id === dynamicSubsection.id ||
          subsection.title.toLowerCase() === dynamicSubsection.title.toLowerCase()
      );

      if (!existingSubsection) {
        const insertAt = targetSection.subsections.findIndex(
          (subsection) => subsection.title.toLowerCase() === "other"
        );
        const nextSubsection = {
          ...dynamicSubsection,
          actions: dynamicSubsection.actions.map((action) => ({ ...action })),
        };
        if (insertAt >= 0) {
          targetSection.subsections.splice(insertAt, 0, nextSubsection);
        } else {
          targetSection.subsections.push(nextSubsection);
        }
        continue;
      }

      const existingActionNames = new Set(
        existingSubsection.actions.map((action) => action.name.toLowerCase())
      );
      for (const action of dynamicSubsection.actions) {
        if (!existingActionNames.has(action.name.toLowerCase())) {
          existingSubsection.actions.push({ ...action });
          existingActionNames.add(action.name.toLowerCase());
        }
      }
    }
  }

  return sections;
};

/** Full pipeline: strip placeholders → merge live RMM config. */
export const buildDocumentationSections = (
  baseSections: DocSection[],
  dynamicSections: DocSection[]
): DocSection[] => mergeDocumentationSections(baseSections, dynamicSections);
